import { getRouteApi } from "@tanstack/react-router";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  RowSelectionState,
  ColumnFiltersState,
  Row,
  SortingState,
  Updater,
} from "@tanstack/react-table";
import {
  DataTable,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Button,
  V2Loader,
  buttonVariants,
  cn,
  SearchInput,
  ScrollArea,
  ScrollBar,
  DataTablePagination,
} from "web-design-system";
import { planKeys, useSipData, useSipStats } from "creso-core/hooks";
import { useCallback, useMemo, useState } from "react";
import { useDebounceValue } from "usehooks-ts";
import { PaginatedResponse, SIPPlan } from "creso-core/types";
import { ChevronDown } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  ConditionalRenderer,
  createSipColumns,
  NoData,
  SipStats,
} from "web-core";
import { SipBulkDialogs, SipRowActions } from "./plan-actions";

export function SIPComponent() {
  const route = getRouteApi("/(auth)/_headed/clients/$id");
  const { client } = route.useLoaderData();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [search, setSearch] = useState("");

  const [debouncedSearch] = useDebounceValue(search, 500);

  const [sorting, setSorting] = useState<SortingState>([]);

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(updater);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const {
    data: sipData,
    isError,
    isLoading,
  } = useSipData(
    client?.investment_account ?? "",
    pagination.pageIndex + 1,
    pagination.pageSize,
    debouncedSearch,
    sorting
  );

  const { data: stats } = useSipStats(client?.investment_account || "");

  const [bulkAction, setBulkAction] = useState<
    "modify" | "pause" | "cancel" | "resume" | "delete" | null
  >(null);

  const columns = useMemo(
    () =>
      createSipColumns({
        selectable: !!bulkAction,
        renderActions: (plan) => <SipRowActions plan={plan} client={client} />,
      }),
    [client, bulkAction]
  );

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const enableRowSelection = useCallback(
    (row: Row<SIPPlan>) => {
      if (!bulkAction) return false;
      if (bulkAction === "modify") return row.original.status === "ACTIVE";
      if (bulkAction === "pause") return row.original.status === "ACTIVE";
      if (bulkAction === "cancel")
        return (
          row.original.status === "ACTIVE" || row.original.status === "PAUSED"
        );
      if (bulkAction === "resume") return row.original.status === "PAUSED";
      if (bulkAction === "delete") return row.original.status === "CANCELLED";
      return false;
    },
    [bulkAction]
  );

  const table = useReactTable({
    data: sipData?.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection,
    enableMultiRowSelection: true,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    rowCount: sipData?.count,
    onPaginationChange: setPagination,
    manualSorting: true,
    enableMultiSort: false,
    onSortingChange,
    getRowId: (row) => row.id?.toString(),
    state: {
      pagination,
      rowSelection,
      columnFilters,
      sorting,
    },
  });

  const queryClient = useQueryClient();

  const selectedPlans = useMemo(() => {
    // SIP list data is cached per page. Partial-match on the "list" key
    // (without page/pageSize) so we gather rows from every cached page.
    const queries = queryClient.getQueriesData<PaginatedResponse<SIPPlan>>({
      queryKey: planKeys.lists("XSIP", client.investment_account ?? ""),
    });

    const selectedById = new Map<string, SIPPlan>();
    queries.forEach(([, data]) => {
      data?.results.forEach((sip) => {
        const id = sip.id.toString();
        if (rowSelection[id]) selectedById.set(id, sip);
      });
    });

    return Array.from(selectedById.values());
  }, [queryClient, client.investment_account, rowSelection]);

  const [activeBulkDialog, setActiveBulkDialog] = useState<
    "modify" | "pause" | "cancel" | "delete" | null
  >(null);

  const resetBulkAction = () => {
    setBulkAction(null);
    setRowSelection({});
  };

  const handleBulkDialogToggle = (show: boolean) => {
    if (!show) {
      setActiveBulkDialog(null);
      resetBulkAction();
    }
  };

  const bulkActionOptions: {
    label: string;
    value: NonNullable<typeof bulkAction>;
  }[] = useMemo(() => {
    const allowedActions: {
      label: string;
      value: NonNullable<typeof bulkAction>;
    }[] = [];
    if (!stats) return allowedActions;
    if (stats.breakdown.status.ACTIVE.count > 1) {
      allowedActions.push({ label: "Modify", value: "modify" });
      allowedActions.push({ label: "Pause", value: "pause" });
      allowedActions.push({ label: "Cancel", value: "cancel" });
    }
    if (stats.breakdown.status.PAUSED.count > 1) {
      allowedActions.push({ label: "Resume", value: "resume" });
    }
    if (stats.breakdown.status.CANCELLED.count > 1) {
      allowedActions.push({ label: "Delete", value: "delete" });
    }
    return allowedActions;
  }, [stats]);

  const handleExecute = () => {
    if (selectedPlans.length === 0) return;
    if (bulkAction === "modify") {
      setActiveBulkDialog("modify");
    }
    if (bulkAction === "pause" || bulkAction === "resume") {
      setActiveBulkDialog("pause");
    }
    if (bulkAction === "cancel") {
      setActiveBulkDialog("cancel");
    }
    if (bulkAction === "delete") {
      setActiveBulkDialog("delete");
    }
  };

  return (
    <V2Loader isLoading={isLoading} className="flex-1 gap-4">
      {isError ||
      !sipData ||
      (sipData?.results.length === 0 && !debouncedSearch) ? (
        <NoData
          description="Get them started. Investment Plans populate here."
          title="No SIPs Yet"
        />
      ) : (
        <>
          <SipStats stats={stats} />
          <div className="flex flex-row gap-2 items-center">
            <div className="w-70 mr-auto">
              <SearchInput
                placeholder="Search by scheme name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <ConditionalRenderer condition={bulkActionOptions.length > 0}>
              {bulkAction ? (
                <>
                  <span className="text-muted-foreground text-sm">
                    {Object.values(rowSelection).filter(Boolean).length}{" "}
                    selected
                  </span>
                  <Button variant="outline" size="sm" onClick={resetBulkAction}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={selectedPlans.length === 0}
                    onClick={handleExecute}
                  >
                    Proceed
                  </Button>
                </>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className={cn(
                      "group",
                      buttonVariants({ variant: "outline" })
                    )}
                  >
                    Actions{" "}
                    <ChevronDown className="transition-transform duration-200 group-data-[state=open]:-rotate-180" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {bulkActionOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onClick={() => {
                          setRowSelection({});
                          setBulkAction(option.value);
                        }}
                      >
                        {option.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <SipBulkDialogs
                client={client}
                plans={selectedPlans}
                activeDialog={activeBulkDialog}
                onToggle={handleBulkDialogToggle}
              />
            </ConditionalRenderer>
          </div>
          <div className="flex flex-col flex-1 w-full overflow-hidden border border-border rounded-lg">
            <ScrollArea className="flex-1 flex flex-col">
              <DataTable
                className={cn(
                  table.getFilteredRowModel().rows.length === 0 && "flex-1",
                  "border-0 flex-1"
                )}
                pagination={false}
                table={table}
              />

              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            <DataTablePagination
              table={table}
              className="sticky bottom-0 z-20"
            />
          </div>
        </>
      )}
    </V2Loader>
  );
}
